// Manages all contact fetching logic

import { useState, useEffect, useCallback, Dispatch, SetStateAction } from "react"
import { useToast } from "@/hooks/use-toast"
import { ApiResponse } from "@/lib/api-response"
import { apiFetch } from "@/lib/api/apiFetch"
import { usePathname } from "next/navigation"

interface Contact {
  _id: string
  email: string
  name: string
  source: string
  location?: string
  status: any
  remarks?: string
  createdAt: string
  updatedAt: string
  [key: string]: any
}

export interface Pagination {
  page: number
  limit: number
  total: number
  pages: number
}

interface UseContactsReturn {
  contacts: Contact[]
  loading: boolean
  error: string | null
  pagination: Pagination
  fetchContacts: (page?: number, search?: string, status?: string, source?: string, location?: string, emailStatus?: string, lists?: string, limit?: number) => Promise<void>
  refreshContacts: () => Promise<void>

  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
  fetchMasterData: () => Promise<void>

  contactActivity: any
  fetchContactActivity: any
  activityPagination: Pagination

  recipientListsList: Array<{ value: string; label: string }>
  fetchRecipientLists: () => Promise<void>

  statusFilter: string
  setStatusFilter: (val: string) => void
  sourceFilter: string
  setSourceFilter: (val: string) => void
  locationFilter: string
  setLocationFilter: (val: string) => void
  emailStatusFilter: string
  setEmailStatusFilter: (val: string) => void
  emailStatusStats: {
    safe: number
    risky: number
    invalid: number
    unverified: number
    bounced: number
  }
  listsFilter: string
  setListsFilter: (val: string) => void
  clearFilters: () => void
  handleLimitChange: (newLimit: number) => void
  handleArchiveMultiple: (ids: string[]) => Promise<boolean>
}

let globalContactFilters = {
  statusFilter: "",
  sourceFilter: "",
  locationFilter: "",
  emailStatusFilter: "",
  listsFilter: ""
}

/**
 * Custom React hook to manage all contact fetching logic.
 *
 * Responsibilities:
 * - Fetching contacts from the backend with pagination support
 * - Searching contacts by keyword
 * - Managing loading and error states
 * - Handling initial fetch and manual refresh
 * - Displaying toast notifications on API errors
 *
 * The hook automatically fetches contacts on initial mount.
 * Subsequent fetches can be triggered manually using `fetchContacts`
 * or `refreshContacts`.
 *
 * @param {number} [initialLimit=15]
 * Number of contacts to fetch per page.
 *
 * @returns {UseContactsReturn} useContactsResult
 *
 * @returns {Contact[]} useContactsResult.contacts
 * List of contacts fetched from the server.
 *
 * @returns {boolean} useContactsResult.loading
 * Indicates whether contact data is currently being fetched.
 *
 * @returns {string | null} useContactsResult.error
 * Error message if fetching contacts fails.
 *
 * @returns {Pagination} useContactsResult.pagination
 * Pagination state including current page, limit, total records, and total pages.
 *
 * @returns {(page?: number, search?: string) => Promise<void>} useContactsResult.fetchContacts
 * Fetches contacts for a specific page and optional search query.
 *
 * @returns {() => Promise<void>} useContactsResult.refreshContacts
 * Re-fetches contacts using the current page and search state.
 * @author uplSouvik
 */

export function useContacts(initialLimit = 15): UseContactsReturn {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: initialLimit,
    total: 1,
    pages: 1,
  })
  const [currentSearch, setCurrentSearch] = useState("")

  // Local state mirrored from global
  const [statusFilter, setStatusFilterState] = useState(globalContactFilters.statusFilter)
  const [sourceFilter, setSourceFilterState] = useState(globalContactFilters.sourceFilter)
  const [locationFilter, setLocationFilterState] = useState(globalContactFilters.locationFilter)
  const [emailStatusFilter, setEmailStatusFilterState] = useState(globalContactFilters.emailStatusFilter)
  const [listsFilter, setListsFilterState] = useState(globalContactFilters.listsFilter)
  const [emailStatusStats, setEmailStatusStats] = useState({
    safe: 0,
    risky: 0,
    invalid: 0,
    unverified: 0,
    bounced: 0
  })


  const setStatusFilter = useCallback((val: string) => {
    globalContactFilters.statusFilter = val
    setStatusFilterState(val)
  }, [])

  const setSourceFilter = useCallback((val: string) => {
    globalContactFilters.sourceFilter = val
    setSourceFilterState(val)
  }, [])

  const setLocationFilter = useCallback((val: string) => {
    globalContactFilters.locationFilter = val
    setLocationFilterState(val)
  }, [])

  const setEmailStatusFilter = useCallback((val: string) => {
    globalContactFilters.emailStatusFilter = val
    setEmailStatusFilterState(val)
  }, [])

  const setListsFilter = useCallback((val: string) => {
    globalContactFilters.listsFilter = val
    setListsFilterState(val)
  }, [])

  const clearFilters = useCallback(() => {
    setStatusFilter("")
    setSourceFilter("")
    setLocationFilter("")
    setEmailStatusFilter("")
    setListsFilter("")
  }, [setStatusFilter, setSourceFilter, setLocationFilter, setEmailStatusFilter, setListsFilter])

  const pathname = usePathname()

  // Clear the filter if changing page (not on contacts page)
  useEffect(() => {
    if (pathname && !pathname.includes('/contacts')) {
      clearFilters()
    }
  }, [pathname, clearFilters])

  const { toast } = useToast()
  const [statusOptionsList, setStatusOptionsList] = useState<Array<{ value: string; label: string }>>([])
  const [sourceOptionsList, setSourceOptionsList] = useState<Array<{ value: string; label: string }>>([])
  const [recipientListsList, setRecipientListsList] = useState<Array<{ value: string; label: string }>>([])
  const [contactActivity, setContactActivity] = useState<Array<any>>([])
  const [activityPagination, setActivityPagination] = useState<Pagination>({
    page: 1,
    limit: 15,
    total: 0,
    pages: 1,
  })

  const fetchContacts = useCallback(async (page = 1, search = "", status = statusFilter, source = sourceFilter, location = locationFilter, emailStatus = emailStatusFilter, lists = listsFilter, limit = pagination.limit) => {
    try {
      setCurrentSearch(search)
      setError(null)
      setPagination(prev => ({
        ...prev,
        page: page,
        limit: limit,
        total: 0,
        pages: 0,
      }))
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(status && { status }),
        ...(source && { source }),
        ...(location && { location }),
        ...(emailStatus && { emailStatus }),
        ...(lists && { lists }),
      })

      const data = await apiFetch(`/api/contacts?${params}`)

      if (data.success) {
        setContacts(data.data)
        if (data.page != null && data.limit != null && data.total != null && data.pages != null) {
          setPagination({
            page: data.page,
            limit: data.limit,
            total: data.total,
            pages: data.pages,
          })
        }
        if (data.stats) {
          setEmailStatusStats(data.stats)
        }
      } else {
        const errorMsg = data.message || "Failed to fetch contacts"
        setError(errorMsg)
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive",
        })
      }
    } catch (err) {
      const errorMsg = "Failed to fetch contacts"
      setError(errorMsg)
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [pagination.limit, toast, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter])

  const refreshContacts = useCallback(() => {
    return fetchContacts(pagination.page, currentSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter, pagination.limit)
  }, [fetchContacts, pagination.page, currentSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter, pagination.limit])

  const handleLimitChange = useCallback((newLimit: number) => {
    fetchContacts(1, currentSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter, newLimit)
  }, [fetchContacts, currentSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter, pagination.limit])


  /**
  * Fetches master data from the API and prepares dropdown options.
  *
  * - Calls `/api/master-data` with pagination and optional search query
  * - Filters the response data by `type`
  *   - `status` → used for status dropdown options
  *   - `source` → used for source dropdown options
  * - Updates component state with formatted `{ value, label }` objects
  * - Shows a toast notification on API or network failure
  *
  * @async
  * @function fetchMasterData
  * @param {number} [page=1] - Page number for pagination
  * @param {string} [search=""] - Optional search keyword to filter master data
  * @returns {Promise<void>} Resolves when data is fetched and state is updated
  */

  const fetchMasterData = async (page = 1, search = "") => {
    const token = localStorage.getItem("token");
    try {

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',
        ...(search && { search }),
      })

      const data = await apiFetch(`/api/master-data?${params}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (data.success) {
        //filter status from master data api
        const statusOptionsFromApi = data.data.filter((item: any) => item?.type === 'status').map((item: any) => ({
          value: item?._id,
          label: item?.name
        }))
        setStatusOptionsList(statusOptionsFromApi)

        //filter source from master data api
        const sourceOptionsFromApi = data.data.filter((item: any) => item?.type === 'source').map((item: any) => ({
          value: item?._id,
          label: item?.name
        }))
        setSourceOptionsList(sourceOptionsFromApi)

      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to fetch Master Data",
          variant: "destructive",
        })
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch Master Data",
        variant: "destructive",
      })
    }
  }

  const fetchRecipientLists = async (page = 1, search = "") => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',
        ...(search && { search }),
      })

      const data = await apiFetch(`/api/recipient-lists?${params}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!data.error) {
        const options = data.lists.map((item: any) => ({
          value: item?._id,
          label: item?.name
        }))
        setRecipientListsList(options)
      } else {
        // toast({
        //   title: "Error",
        //   description: data.message || "Failed to fetch Recipient Lists",
        //   variant: "destructive",
        // })
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch Recipient Lists",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchContactActivity = useCallback(async (
    contactId: string,
    params: {
      page?: number;
      limit?: number;
      action?: string;
      actions?: string;
      startDate?: string;
      endDate?: string;
    } = {}
  ) => {
    try {
      setLoading(true)
      const { page = 1, limit = activityPagination.limit, ...filters } = params

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== undefined))
      })

      const data = await apiFetch(`/api/activity-logs/${contactId}?${queryParams}`)

      if (!data.error) {
        setContactActivity(data.data || [])
        if (data.pagination) {
          setActivityPagination(data.pagination)
        }
      } else {
        const errorMsg = data.message || "Failed to fetch activity logs"
        setError(errorMsg)
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive",
        })
      }
    } catch (err) {
      const errorMsg = "Failed to fetch activity logs"
      setError(errorMsg)
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [activityPagination.limit, toast])

  const handleArchiveMultiple = async (ids: string[]) => {
    try {
      const data = await apiFetch(`/api/contacts/bulk-action`, {
        method: "PUT",
        data: {
          ids: ids,
          action: "archive",
        }
      })
      if (!data.error) {
        toast({
          title: "Success",
          description: "Contacts archived successfully",
        })
        refreshContacts()
        return true
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to archive contacts",
          variant: "destructive",
        })
        return false
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to archive contacts",
        variant: "destructive",
      })
      return false
    }
  }

  return {
    contacts,
    loading,
    error,
    pagination,
    fetchContacts,
    refreshContacts,

    // master data to show in dropdown
    statusOptionsList,
    sourceOptionsList,
    fetchMasterData,
    recipientListsList,
    fetchRecipientLists,

    // contact activity
    contactActivity,
    fetchContactActivity,
    activityPagination,

    statusFilter,
    setStatusFilter,
    sourceFilter,
    setSourceFilter,
    locationFilter,
    setLocationFilter,
    // clearFilters,
    handleLimitChange,
    emailStatusFilter,
    setEmailStatusFilter,
    emailStatusStats,
    listsFilter,
    setListsFilter,
    clearFilters,
    handleArchiveMultiple
  }
}